import { LandTechEndpoints, routedClientWithoutTypenames } from "react-migration/lib/persistence/apollo";
import { LetterEventName, logLetterEvent } from "../../letters/helpers/logLetterEvent";
import { useCallback, useState } from "react";

import SEND_SITE_LETTERS from "react-migration/domains/sites/apollo/sites-api/mutations/sendSiteLetters.gql";
import { SendLettersResponseData } from "../../typings/apollo";
import { SiteLetterInput } from "../../typings/Letter";
import { captureException } from "src/js/util/monitoring";
import { getPaperFormat } from "../../helpers/getPaperFormat";
import { letterStore } from "src/js/stores/letter/store";
import { toCreateLetterContentDTO } from "../../helpers/toCreateLetterContentDTO";
import { useMutation } from "@apollo/client";
import { useSnapshot } from "valtio";

export enum SendStatus {
  SENDING = "sending",
  SENT = "sent",
  ERROR = "error",
}

export type UseSendLettersHook = {
  sendStatus?: SendStatus | null;
  sendLetters: () => Promise<void>;
  clearSendStatus: () => void;
};

export function useSendLetters(): UseSendLettersHook {
  const { recipients, selectedRecipientIds, selectedStageIds, selectedSiteIds, ...letterProps } =
    useSnapshot(letterStore);

  const [sendStatus, setSendStatus] = useState<SendStatus | null>(null);

  const [sendLettersRequest] = useMutation<SendLettersResponseData>(SEND_SITE_LETTERS, {
    client: routedClientWithoutTypenames,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
  });

  const clearSendStatus = () => setSendStatus(null);

  const sendLetters = useCallback(async () => {
    setSendStatus(SendStatus.SENDING);

    const distinctSiteIds = new Set();
    const siteLetterInputs: SiteLetterInput[] = (recipients ?? [])
      .filter((recipient) => selectedRecipientIds?.includes(recipient._id))
      .map(({ siteBoundaryImageUrl, ...recipient }) => {
        // side effect - naughty
        distinctSiteIds.add(recipient.siteId);
        return toCreateLetterContentDTO(
          {
            ...letterProps,
            siteBoundaryImage: siteBoundaryImageUrl,
          },
          recipient,
          {
            hideRecipientAddress: true,
            paperFormat: getPaperFormat(recipient.countryCode),
          }
        );
      });

    try {
      const { data } = await sendLettersRequest({
        variables: { siteLetterInputs },
      });
      setSendStatus(SendStatus.SENT);
      const siteLettersResponses = data?.sendSiteLetters;
      if (siteLettersResponses?.length) {
        logLetterEvent({
          name: LetterEventName.LETTERS_FORM_FINISH_AND_SEND_STEP_SENT_SUCCESSFULLY,
          context: {
            siteCount: distinctSiteIds.size,
            letterCount: siteLetterInputs.length,
          },
        });
      }
    } catch (error) {
      setSendStatus(SendStatus.ERROR);
      logLetterEvent({
        name: LetterEventName.LETTERS_FORM_FINISH_AND_SEND_STEP_SEND_FAILED,
        context: {
          siteCount: distinctSiteIds.size,
          letterCount: siteLetterInputs.length,
        },
      });
      captureException(error, {
        info: "Send letter failure",
        siteIds: selectedSiteIds,
        stageIds: selectedStageIds,
        letter: letterProps.letter,
        letterName: letterProps.letterName,
        pageCount: letterProps.pageCount,
      });
    }
  }, [recipients, selectedRecipientIds, letterProps, sendLettersRequest, selectedSiteIds, selectedStageIds]);

  return {
    sendStatus,
    clearSendStatus,
    sendLetters,
  };
}
